/* eslint-disable no-console */

// Self-heal stale-deploy / poisoned-service-worker clients.
//
// Symptom: after a new deploy, a client running an old index.html (often pinned
// by a stale service-worker precache) clicks a nav item -> router does a dynamic
// import() of a content-hashed chunk whose hash no longer exists on the server
// -> the request fails (404, or worse: HTML served as JS -> "Unexpected token
// '<'"). A normal Ctrl+R re-arms the same broken state; only Ctrl+Shift+R fixes
// it because it bypasses the service worker.
//
// This handler detects that failure mode and recovers automatically: it
// unregisters service workers, clears their caches, and reloads once. Guarded
// so it can never loop.

const GUARD_KEY = "__chunk_reload_at";
const GUARD_WINDOW_MS = 15000; // don't attempt more than once per 15s

function looksLikeChunkError(message: string): boolean {
  if (!message) return false;
  return (
    /Loading (?:CSS )?chunk [^\s]+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    // HTML (index.html) served where JS was expected
    /Unexpected token '<'/i.test(message) ||
    /expected expression, got '<'/i.test(message) ||
    /'<' is not valid/i.test(message)
  );
}

async function dePoisonAndReload(): Promise<void> {
  try {
    const last = Number(sessionStorage.getItem(GUARD_KEY) || 0);
    const now = Date.now();
    if (now - last < GUARD_WINDOW_MS) {
      // Already tried very recently. Stop here to avoid a reload loop; surface
      // the original error to the user/console instead of spinning.
      console.warn("[chunk-reload] recovery already attempted; not reloading again");
      return;
    }
    sessionStorage.setItem(GUARD_KEY, String(now));

    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => undefined)));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => undefined)));
    }
  } catch (e) {
    console.warn("[chunk-reload] cleanup failed, reloading anyway", e);
  } finally {
    window.location.reload();
  }
}

export function installChunkReloadGuard(): void {
  // Rejected dynamic import() (router lazy load) lands here.
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as { message?: string } | undefined;
    const message: string = reason?.message ?? String(event.reason ?? "");
    if (looksLikeChunkError(message)) {
      void dePoisonAndReload();
    }
  });

  // Script/runtime errors (e.g. a chunk parsed as HTML) land here.
  window.addEventListener("error", (event) => {
    const err = event.error as { message?: string } | undefined;
    const message: string = err?.message ?? event.message ?? "";
    if (looksLikeChunkError(message)) {
      void dePoisonAndReload();
    }
  });
}
